import React, { useState, useEffect, useCallback } from 'react'
import { ListChecks, CheckCircle2, Circle, ChevronRight, RefreshCw } from 'lucide-react'
import { supabase } from '../supabase'

const GOLD = '#c9a84c'
const GREEN = '#4a6741'

const TIER_ORDER = [
  'dreamers', 'budding_star', 'debt_crusher', 'shining_star', 'achievers_club',
  'game_changer', 'wealth_builder', 'visionaries', 'luminaries', 'trailblazers', 'pioneers', 'tycoons',
]

const TIER_META = {
  dreamers:       { label: 'Dreamers Club',        emoji: '🎭' },
  budding_star:   { label: 'Budding Star Club',    emoji: '⭐' },
  debt_crusher:   { label: 'Debt Crusher Club',    emoji: '💪' },
  shining_star:   { label: 'Shining Star Club',    emoji: '✨' },
  achievers_club: { label: "Achiever's Club",      emoji: '🏆' },
  game_changer:   { label: 'Game Changer Club',    emoji: '🎯' },
  wealth_builder: { label: 'Wealth Builder Club',  emoji: '💰' },
  visionaries:    { label: 'Visionaries Club',     emoji: '🔮' },
  luminaries:     { label: 'Luminaries Club',      emoji: '💡' },
  trailblazers:   { label: 'Trailblazers Club',    emoji: '🔥' },
  pioneers:       { label: 'Pioneers Club',        emoji: '🚀' },
  tycoons:        { label: 'Tycoons Club',         emoji: '👑' },
}

// Debt-reduction % target for the lower tiers
const DEBT_TARGETS = { dreamers: 10, budding_star: 25, debt_crusher: 50, shining_star: 75, achievers_club: 100 }
// Investment-vs-Emergency-Fund multiple for Wealth Builder and above
const INVESTMENT_MULTIPLES = { wealth_builder: 1, visionaries: 10, luminaries: 25, trailblazers: 50, pioneers: 75, tycoons: 100 }

function fmtShort(n) {
  n = Number(n) || 0
  if (n >= 100000) return '₹' + (n / 100000).toFixed(1) + 'L'
  if (n >= 1000) return '₹' + (n / 1000).toFixed(1) + 'k'
  return '₹' + Math.round(n)
}

function describeNextTarget(tierKey) {
  if (DEBT_TARGETS[tierKey] != null) return `Reach ${DEBT_TARGETS[tierKey]}% debt reduction`
  if (tierKey === 'game_changer') return 'Debt-free + 12mo Emergency Fund + both insurances + start investing'
  if (INVESTMENT_MULTIPLES[tierKey] != null) return `Investment ≥ ${INVESTMENT_MULTIPLES[tierKey]}x Emergency Fund`
  return ''
}

export default function ActionButton({ session, onNavigate }) {
  const userId = session?.user?.id
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(true)
  const [tier, setTier] = useState('dreamers')
  const [stats, setStats] = useState(null)

  const loadData = useCallback(async () => {
    if (!userId) return
    setLoading(true)

    const [membershipRes, loansRes, efRes, policiesRes, investmentsRes] = await Promise.all([
      supabase.from('memberships').select('tier').eq('user_id', userId).single(),
      supabase.from('loans').select('balance, original_amount, joining_balance').eq('user_id', userId),
      supabase.from('emergency_fund').select('current_amount, target_amount, months_target').eq('user_id', userId).maybeSingle(),
      supabase.from('insurance_policies').select('type, is_active').eq('user_id', userId).eq('is_active', true),
      supabase.from('investments').select('current_value, is_active').eq('user_id', userId).eq('is_active', true),
    ])

    setTier(membershipRes.data?.tier || 'dreamers')

    const loans = loansRes.data || []
    const totalJoining = loans.reduce((s, l) => s + parseFloat(l.joining_balance || l.original_amount || 0), 0)
    const totalBalance = loans.reduce((s, l) => s + parseFloat(l.balance || 0), 0)
    const debtReductionPct = totalJoining > 0
      ? Math.min(100, Math.max(0, Math.round(((totalJoining - totalBalance) / totalJoining) * 100)))
      : 0
    const debtFree = loans.length > 0 && totalBalance <= 0

    const ef = efRes.data
    const efTargetAmount = parseFloat(ef?.target_amount) || 0
    const efMonthsCovered = ef && efTargetAmount > 0
      ? (parseFloat(ef.current_amount || 0) * (ef.months_target || 0)) / efTargetAmount
      : 0

    const policies = policiesRes.data || []
    const hasHealthInsurance = policies.some(p => (p.type || '').toLowerCase().includes('health'))
    const hasTermInsurance = policies.some(p => (p.type || '').toLowerCase().includes('term'))

    const investments = investmentsRes.data || []
    const totalInvestmentValue = investments.reduce((s, i) => s + parseFloat(i.current_value || 0), 0)
    const hasStartedInvesting = totalInvestmentValue > 0

    setStats({
      debtReductionPct, debtFree, efMonthsCovered, efTargetAmount,
      hasHealthInsurance, hasTermInsurance, totalInvestmentValue, hasStartedInvesting,
    })
    setLoading(false)
  }, [userId])

  useEffect(() => { if (open) loadData() }, [open, loadData])

  function buildChecklist() {
    if (!stats) return []

    if (DEBT_TARGETS[tier] != null) {
      const target = DEBT_TARGETS[tier]
      return [
        {
          label: `Reach ${target}% debt reduction`,
          sub: `You're at ${stats.debtReductionPct}%` + (stats.debtReductionPct < target ? ` — ${target - stats.debtReductionPct}% to go` : ''),
          done: stats.debtReductionPct >= target,
          page: 'loans',
        },
        { label: 'Review your Budget Planner', sub: 'Find more to put toward your debt', done: false, page: 'budget', habit: true },
        { label: 'Check your Net Worth', sub: 'Track your overall financial progress', done: false, page: 'networth', habit: true },
      ]
    }

    // Game Changer and every tier above it share this base checklist
    const items = [
      { label: 'Become fully debt-free', sub: stats.debtFree ? 'Done!' : `You're at ${stats.debtReductionPct}% reduction`, done: stats.debtFree, page: 'loans' },
      { label: 'Build a 12-month Emergency Fund', sub: `Currently covers ${stats.efMonthsCovered.toFixed(1)} months`, done: stats.efMonthsCovered >= 12, page: 'emergency' },
      { label: 'Add Family Health Insurance', sub: stats.hasHealthInsurance ? 'Added' : 'Not added yet', done: stats.hasHealthInsurance, page: 'insurance' },
      { label: 'Add Term Insurance', sub: stats.hasTermInsurance ? 'Added' : 'Not added yet', done: stats.hasTermInsurance, page: 'insurance' },
    ]

    if (tier === 'game_changer') {
      items.push({ label: 'Start investing (any amount)', sub: stats.hasStartedInvesting ? 'Started' : 'Not started yet', done: stats.hasStartedInvesting, page: 'investments' })
    } else if (INVESTMENT_MULTIPLES[tier] != null) {
      const multiple = INVESTMENT_MULTIPLES[tier]
      const needed = multiple * stats.efTargetAmount
      items.push({
        label: `Investment ≥ ${multiple}x Emergency Fund`,
        sub: stats.efTargetAmount > 0 ? `${fmtShort(stats.totalInvestmentValue)} of ${fmtShort(needed)} needed` : 'Set an Emergency Fund target first',
        done: stats.efTargetAmount > 0 && stats.totalInvestmentValue >= needed,
        page: 'investments',
      })
    }

    // Persistent useful-habit items for all paid tiers
    items.push(
      { label: 'Review your Budget Planner', sub: 'Spot where money can go toward goals', done: false, page: 'budget', habit: true },
      { label: 'Check your Net Worth', sub: 'See your overall financial progress', done: false, page: 'networth', habit: true },
    )

    return items
  }

  function go(page) {
    setOpen(false)
    if (onNavigate) onNavigate(page)
  }

  const tierIndex = TIER_ORDER.indexOf(tier)
  const nextTierKey = TIER_ORDER[tierIndex + 1]
  const checklist = buildChecklist()
  const meta = TIER_META[tier] || TIER_META.dreamers

  return (
    <div style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(v => !v)}
        title="Your action steps"
        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '0.4rem', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '6px', transition: 'color 0.15s, background 0.15s' }}
        onMouseEnter={e => { e.currentTarget.style.color = GOLD; e.currentTarget.style.background = 'var(--surface-hdr)' }}
        onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-muted)'; e.currentTarget.style.background = 'none' }}
      >
        <ListChecks size={17} strokeWidth={1.75} />
      </button>

      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 998 }} />
          <div style={{ position: 'absolute', top: '130%', right: 0, width: '320px', maxWidth: '90vw', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '10px', boxShadow: '0 12px 40px rgba(0,0,0,0.18)', zIndex: 999, padding: '1rem 1.1rem' }}>
            <div style={{ fontFamily: 'monospace', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: '0.3rem' }}>
              Your Action Steps
            </div>
            <div style={{ fontFamily: 'Georgia,serif', fontSize: '1rem', fontWeight: '700', marginBottom: '0.85rem' }}>
              {meta.emoji} {meta.label}
            </div>

            {loading ? (
              <div style={{ fontFamily: 'monospace', fontSize: '0.72rem', color: 'var(--text-muted)' }}>Loading...</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {checklist.map((item, i) => (
                  <button
                    key={i}
                    onClick={() => go(item.page)}
                    style={{
                      display: 'flex', alignItems: 'flex-start', gap: '0.5rem', textAlign: 'left',
                      background: item.done ? 'transparent' : 'var(--surface-hdr)',
                      border: 'none', borderRadius: '6px', padding: '0.5rem 0.6rem',
                      cursor: 'pointer', width: '100%',
                    }}
                  >
                    {item.habit
                      ? <RefreshCw size={15} color={GOLD} style={{ flexShrink: 0, marginTop: '2px' }} />
                      : item.done
                        ? <CheckCircle2 size={16} color={GREEN} style={{ flexShrink: 0, marginTop: '1px' }} />
                        : <Circle size={16} color="var(--text-muted)" style={{ flexShrink: 0, marginTop: '1px' }} />
                    }
                    <div style={{ flex: 1 }}>
                      <div style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.8rem', color: item.done && !item.habit ? 'var(--text-muted)' : 'var(--text)', textDecoration: item.done && !item.habit ? 'line-through' : 'none', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                        {item.label}
                        {item.habit && <span style={{ fontFamily: 'monospace', fontSize: '0.55rem', color: GOLD, background: 'rgba(201,168,76,0.1)', padding: '0.1rem 0.35rem', borderRadius: '99px' }}>habit</span>}
                      </div>
                      <div style={{ fontFamily: 'monospace', fontSize: '0.62rem', color: 'var(--text-muted)', marginTop: '0.1rem' }}>
                        {item.sub}
                      </div>
                    </div>
                    <ChevronRight size={14} color="var(--text-muted)" style={{ flexShrink: 0, marginTop: '2px' }} />
                  </button>
                ))}
              </div>
            )}

            {nextTierKey && (
              <div style={{ marginTop: '0.9rem', paddingTop: '0.75rem', borderTop: '1px solid var(--border)', fontFamily: 'monospace', fontSize: '0.64rem', color: GOLD }}>
                Next: {TIER_META[nextTierKey].emoji} {TIER_META[nextTierKey].label} — {describeNextTarget(nextTierKey)}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}
